import React from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ConfigProvider, notification } from "antd";
import { IntlProvider } from "react-intl";
import themes from "./settings/themes";
import { build_color_palette } from "./settings/themes/colors";
import AppLocale from "./languageProvider";
import { config, siteConfig } from "./settings/config";
import GlobalTheme from "./globalTheme";
import { Helmet } from "react-helmet";

import SplashScreen from "./splashScreen";
import PublicRoutes from "./router";
import {
  authUser,
  loginUser,
  getUserInfo,
  removeAuthToken,
  getUserState,
  setUserTokens,
  getDeviceToken,
  setDeviceToken,
  axiosCancel,
  invokeApi,
} from "./helpers/authHelper";
import { get_app_templates, get_app_custom, get_app_features } from "./helpers/appHelper";

import moment from "moment";
import numeral from "numeral";
import * as d3 from "d3";

import { check_icon_imported } from "./faIcons";

import { Alert } from "antd";
import { ExclamationCircleOutlined, DownOutlined, UpOutlined, CloseOutlined } from "@ant-design/icons";

function getView(width) {
  let newView = "MobileView";
  if (width > 1500) {
    newView = "DesktopView";
  } else if (width > 767) {
    newView = "TabView";
  }
  return newView;
}

class App extends React.Component {
  constructor(props) {
    super(props);

    // defaultPage should be controlled via backend so different users types can have different default pages
    this.state = {
      theme: siteConfig.defaultTheme,
      defaultPage: siteConfig.defaultPage,

      currentLocale: AppLocale[config.defaultLanguage || "en"],
      languages: [
        {
          key: "en",
          label: { EN: "English", FR: "Anglais" },
          order: 0,
          value: "EN",
          full_toggle_label: "English",
        },
        {
          key: "fr",
          label: { EN: "French", FR: "Français" },
          order: 1,
          value: "FR",
          full_toggle_label: "Français",
        },
      ],
      selectedLanguage: null,

      showSplashScreen: true,
      isAppLoading: false,
      isAuthenticating: true,
      isAuthenticated: false,
      loading_state: false,

      user_state: null,
      state_order: ["sq", "token", "tc"],
      active_role: "all",
      roles: {},
      permissions: {},

      app_dates: null,
      documents: null,

      view: getView(window.innerWidth),
      height: window.innerHeight,
      printView: false,

      features: {},
      isFeaturesLoading: true,

      appLayout: "panel",
      pageTemplates: null,
      isCustomLoading: true,
      customLang: null,
      customTheme: null,

      sbCollapsed: window.innerWidth > 1500 ? false : true,
      sbOpenDrawer: false,
      sbCurrent: [],
      sbOpenKeys: [],

      breadcrumb: [],
      breadcrumb_ext: [],
    };
  }

  async componentDidMount() {
    window.addEventListener("beforeprint", event => {
      this.printSetup(event);
    });
    window.addEventListener("afterprint", event => {
      this.printSetup(event);
    });
    this.setState({ isAppLoading: true, loading_state: "inital" });
  }

  async componentDidUpdate(prevProps, prevState) {
    if (prevState.isAppLoading !== this.state.isAppLoading && this.state.isAppLoading) {
      this.load_app();
    }
  }

  load_app = async () => {
    // App Authentication Calls
    if (this.state.isAuthenticating && !this.state.isAuthenticated) {
      await this.update_user_status();
      this.setState({ isAuthenticating: false });
    }

    //Post Authenication Loading Calls
    if (this.state.isAuthenticated) {
      if (this.state?.pageTemplates === null) {
        var templates = await get_app_templates();
        this.setState({ pageTemplates: templates });
      }

      if (this.state?.isFeaturesLoading) {
        await this.load_app_features();
      }

      if (this.state?.isCustomLoading) {
        await this.load_app_custom();
      }

      if (this?.state?.app_dates === null) {
        var dates = await this.get_app_dates();
        this.setState({ app_dates: dates });
      }
      if (this.state.selectedLanguage === null) {
        let lang = localStorage.getItem("app.language") ?? (config.defaultLanguage || "en");
        this.setState({
          selectedLanguage: lang?.toUpperCase(),
          currentLocale: AppLocale?.[lang.toLowerCase()] ?? AppLocale?.en,
        });
      }
    }

    // Complete loading check
    if (this.state.isAppLoading && this.app_load_complete()) {
      this.setState({ showSplashScreen: false, isAppLoading: false, loading_state: "complete" });
    }
  };

  //Resets the application state
  clear_app() {
    this.setState({
      defaultPage: siteConfig.defaultPage,

      user_state: null,
      state_order: ["sq", "token", "tc"],
      active_role: "all",
      roles: {},
      permissions: {},

      features: {},
      isFeaturesLoading: true,

      documents: null,

      pageTemplates: null,
      sbOpenDrawer: false,
      sbCurrent: [],
      sbOpenKeys: [],

      breadcrumb: [],
      breadcrumb_ext: [],
    });
  }

  printSetup(event) {
    this.setState({ printView: event.type === "beforeprint" ? true : false });
  }

  // Checks that the application elements are loaded
  app_load_complete = () => {
    return (
      // Authenication complete, not logged in
      (!this.state.isAuthenticated && !this.state.isAuthenticating) ||
      // Authenication complete and logged in
      (this.state.isAuthenticated &&
        !this.state.isAuthenticating &&
        // Check State Items
        this.state.user_state !== null &&
        !this.state.isFeaturesLoading &&
        this.state.pageTemplates !== null &&
        !this.state.isCustomLoading)
    );
  };

  get_app_dates = async () => {
    let dates = invokeApi({
      path: "/rest/app/dates",
      method: "GET",
      queryParams: {
        cat: "risk7",
      },
    });
    return await dates;
  };

  get_document_count = async () => {
    return await invokeApi({
      path: "/rest/portal/document/count",
      method: "GET",
      onError: e => {
        notification.error({
          message: "Error loading document count",
          duration: 5,
        });
      },
    });
  };

  update_document_count = async () => {
    var documents = await this.get_document_count();
    if (documents !== null && documents !== undefined) {
      this.setState({
        documents: documents,
      });
    }
  };

  update_user_status = async () => {
    if (await authUser()) {
      var userState = await getUserInfo();
      if (userState && !userState?.user_info?.is_dz_user) {
        localStorage.removeItem("fef.auth.device");
        this.signoutUser();
      } else if (userState) {
        var documents = await this.get_document_count();
        if (!this.state.isAuthenticated) {
          this.setState({
            isAppLoading: true,
            showSplashScreen: true,
            isAuthenticated: true,
            loading_state: "user_data",
            documents: documents ?? 0,
            ...userState,
          });
        } else {
          this.setState({
            isAuthenticated: true,
            loading_state: "user_data",
            documents: documents ?? 0,
            ...userState,
          });
        }
      }
    }
  };

  load_app_templates = async () => {
    var templates = await get_app_templates();
    this.setState({ pageTemplate: templates });
    return templates;
  };

  load_app_features = async () => {
    var appFeatures = await get_app_features();
    this.setState({
      features: appFeatures?.products ?? {},
      defaultPage: appFeatures?.products?.dz ? appFeatures?.productPages?.dz : appFeatures?.productPages?.surveyEngine,
      isFeaturesLoading: false,
    });
  };

  load_app_custom = async () => {
    var custom = await get_app_custom();
    this.setState({
      isCustomLoading: false,
      customLang: custom?.lang ?? [],
      customTheme: custom?.theme ?? {},
    });
  };

  update_user_state = async () => {
    if (this.state.isAuthenticated) {
      let s = await getUserState();

      this.setState({ user_state: s });
    }
  };

  user_has_authenticated = value => {
    this.setState({ isAuthenticated: value });
  };

  signoutUser = () => {
    removeAuthToken();
    this.setState({ isAuthenticated: false });
    this.clear_app();
  };

  changeActiveRole = role => {
    this.setState({ active_role: role });
  };

  // Function to handle sidebar and topbar nav.
  toggleResize = (width, height) => {
    let view = getView(width);
    this.setState({ view: view, sbCollapsed: view !== "DesktopView", height: height });
  };

  toggleCollapsed = () => {
    this.setState({ sbCollapsed: !this.state.sbCollapsed });
  };

  currentLanguage = () => {
    return config.defaultLanguage;
  };

  getSelectedLanguage = () => {
    return this.state.selectedLanguage ?? (config?.defaultLanguage.toUpperCase() || "EN");
  };

  getLanguages = () => {
    return this.state.languages;
  };

  changeLanguage = lang => {
    this.setState({
      currentLocale: AppLocale?.[lang.toLowerCase()] ?? AppLocale?.en,
      selectedLanguage: lang,
    });
    localStorage.setItem("app.language", lang?.toUpperCase?.());
  };

  capitalizeStr = s => {
    let str = "";

    s.split(" ").map((ss, i) => (str += ss.charAt(0).toUpperCase() + ss.slice(1) + " "));

    return str.trim();
  };

  hasPermission = permission => {
    return this.state.permissions[this.state.active_role] !== undefined &&
      this.state.permissions[this.state.active_role].indexOf(permission) !== -1
      ? true
      : false;
  };

  updateLayout = layout => {
    this.setState({ appLayout: layout });
  };

  buildTheme = style => {
    let theme = themes?.[style] ?? {};

    if (this.state?.customTheme?.[style] ?? false) {
      let { palette_config, ...custom } = this.state?.customTheme?.[style] ?? {};
      if (palette_config ?? false) {
        let palette = build_color_palette(palette_config);
        theme["color"] = { ...(theme?.color ?? {}), ...palette };
      }

      theme = { ...theme, ...custom };
    }
    return theme;
  };

  getPageTemplate = async page => {
    let templates = this.state.pageTemplates;
    if (templates === null) {
      templates = await this.load_app_templates();
    }
    return templates?.[page] ?? false;
  };

  intlFormatData = (lan_block, description, key = "display_name") => {
    let iso_lan = this.state.currentLocale;
    Object.keys(lan_block ?? {}).forEach(k => (lan_block[k.toUpperCase()] = lan_block[k]));
    let msg =
      lan_block && lan_block[iso_lan.app.toUpperCase()] && lan_block[iso_lan.app.toUpperCase()][key]
        ? lan_block[iso_lan.app.toUpperCase()][key]
        : description;
    return msg;
  };

  getBanner = () => {
    if (
      this.state.banner_info?.enabled &&
      !this.state.isAppLoading &&
      this.state.isAuthenticated &&
      !this.state.isAuthenticating &&
      this.state.user_state !== null
    ) {
      if (this.state.view === "MobileView") {
        return (
          <Alert
            showIcon={false}
            closeText={<CloseOutlined className="closeIcon" />}
            message={
              <div>
                <div className="mobileMessage">
                  <ExclamationCircleOutlined className="mobileAlertIcon" />
                  <span className="mobileLabel">
                    {this.intlFormatData(
                      this.state.banner_info?.[this.state.banner_info?.active_msg]?.iso_language,
                      "--",
                      "label"
                    )}
                  </span>
                </div>
              </div>
            }
            description={
              <div>
                {" "}
                {!this.state.showMore && (
                  <div className="viewMore" onClick={() => this.setState({ showMore: true })}>
                    View more <DownOutlined className="viewMoreSize" />
                  </div>
                )}
                {this.state.showMore && (
                  <div className="mobileDescription">
                    <div className="descriptionFont">
                      {this.intlFormatData(
                        this.state.banner_info?.[this.state.banner_info?.active_msg]?.iso_language,
                        "--",
                        "description"
                      )}
                    </div>
                    <button onClick={() => this.setState({ showMore: false })} className="viewLess">
                      View less <UpOutlined className="viewLessSize" />
                    </button>
                  </div>
                )}
              </div>
            }
            banner
            closable
          />
        );
      } else if (this.state.view === "TabView") {
        return (
          <Alert
            message={
              <span className="alertLabel">
                {this.intlFormatData(
                  this.state.banner_info?.[this.state.banner_info?.active_msg]?.iso_language,
                  "--",
                  "label"
                )}
                :
              </span>
            }
            description={
              <span className="alertDescription">
                {this.intlFormatData(
                  this.state.banner_info?.[this.state.banner_info?.active_msg]?.iso_language,
                  "--",
                  "description"
                )}
              </span>
            }
            banner
            closable
            closeText={<CloseOutlined className="closeIcon" />}
            icon={
              <div className="tabletAlertIcon">
                <ExclamationCircleOutlined className="alertIconSize" />
              </div>
            }
          />
        );
      } else {
        return (
          <Alert
            message={
              <div>
                <span className="alertLabel">
                  {this.intlFormatData(
                    this.state.banner_info?.[this.state.banner_info?.active_msg]?.iso_language,
                    "--",
                    "label"
                  )}
                  :
                </span>{" "}
                <span className="alertDescription">
                  {this.intlFormatData(
                    this.state.banner_info?.[this.state.banner_info?.active_msg]?.iso_language,
                    "--",
                    "description"
                  )}
                </span>
              </div>
            }
            banner
            closable
            icon={
              <div className="alertIcon">
                <ExclamationCircleOutlined className="alertIconSize" />
              </div>
            }
          />
        );
      }
    } else {
      return null;
    }
  };

  render() {
    moment.locale(this.state.currentLocale.moment);
    numeral.locale(this.state.currentLocale.numeral);
    d3.formatDefaultLocale(this.state.currentLocale.d3);

    let context = {
      ...this.state,
      app_container: this.app_container,
      login_user: loginUser,
      signout_user: this.signoutUser,
      load_app: this.load_app,
      load_app_custom: this.load_app_custom,
      setUserTokens: setUserTokens,
      updateUserStatus: this.update_user_status,
      updateUserState: this.update_user_state,
      user_has_authenticated: this.user_has_authenticated,
      getDeviceToken: getDeviceToken,
      setDeviceToken: setDeviceToken,
      toggleCollapsed: this.toggleCollapsed,
      toggleResize: this.toggleResize,
      changeActiveRole: this.changeActiveRole,
      checkIconImported: check_icon_imported,
      capitalizeStr: this.capitalizeStr,
      currentProduct: this.currentProduct,
      axiosCancel: axiosCancel,
      hasPermission: this.hasPermission,
      updateLayout: this.updateLayout,
      getPageTemplate: this.getPageTemplate,
      intlFormatData: this.intlFormatData,
      getAppDates: this.get_app_dates,
      currentLanguage: this.currentLanguage,
      getSelectedLanguage: this.getSelectedLanguage,
      getLanguages: this.getLanguages,
      changeLanguage: this.changeLanguage,
      updateDocumentCount: this.update_document_count,
      getBanner: this.getBanner,
    };

    return (
      <ConfigProvider locale={this.state.currentLocale.antd}>
        <IntlProvider
          locale={this.state.currentLocale.locale}
          defaultLocale={"en-US"}
          messages={{
            ...this.state.currentLocale.messages,
            ...(this.state?.customLang?.[this.state.currentLocale.locale] ?? {}),
          }}
        >
          <ThemeProvider theme={this.buildTheme(this.state.theme)}>
            <GlobalTheme />
            <Helmet>
              <title>{siteConfig.title}</title>
            </Helmet>
            <SplashScreen show={this.state.showSplashScreen} />
            {!this.state.isAppLoading ? (
              <BrowserRouter>
                <PublicRoutes context={context} />
              </BrowserRouter>
            ) : (
              false
            )}
          </ThemeProvider>
        </IntlProvider>
      </ConfigProvider>
    );
  }
}

export default App;
